<script lang="ts">
  import { createEventDispatcher, tick } from 'svelte';
  import { isKeyCombination, getCodeFromEvent } from '../../utils/keyboard-event.js';
  import { getMenuPosition } from './positioning.js';

  /**
   * The id of the menu. Has to be the same value as the value of the
   * `aria-controls` attribute on the element that triggers the menu.
   */
  export let id: string;

  /**
   * A description of the menu. Used as an accessible label.
   */
  export let label: string;

  /**
   * Whether the menu is open.
   *
   * Note: This component does not handle the open state by itself. When the
   * menu should close (e.g. when the user clicks outside or presses the Escape
   * key), it emits a `close` event. It does not change the value of `open`.
   */
  export let open: boolean;

  /**
   * The element above or below which the menu is placed.
   */
  export let anchorElement: HTMLElement;

  let maxHeight: string | undefined;

  /**
   * position the menu above or below the anchor-element
   * @default false
   */
  let displayBelow = false;

  $: {
    if (open) {
      focusFirstMenuItem();
      updatePositioning();
    } else {
      /* ESLint does not recognize the bind(maxHeight) syntax in the styles */
      /* eslint-disable @typescript-eslint/no-unused-vars */
      maxHeight = undefined;
    }
  }

  const updatePositioning = () => {
    const position = getMenuPosition(anchorElement, menuElement);

    displayBelow = position.displayBelow;

    if (position.maxHeight) {
      // 8px big is the gap we want between the menu and the viewport
      maxHeight = `${position.maxHeight - 8}px`;
    }
  };

  const dispatch = createEventDispatcher();

  let menuElement: HTMLElement;

  const closeMenu = () => {
    dispatch('close');
  };

  const getMenuItems = () => {
    const all: HTMLElement[] = Array.from(menuElement.querySelectorAll('[role="menuitemradio"]'));
    const focusedIndex = all.findIndex((menuItem) => document.activeElement === menuItem);
    const focused = focusedIndex === -1 ? undefined : all[focusedIndex];

    return {
      all,
      focused,
      focusedIndex,
    };
  };

  const focusFirstMenuItem = async () => {
    const { all } = getMenuItems();

    await tick();
    all[0]?.focus();
  };

  const focusLastMenuItem = () => {
    const { all } = getMenuItems();

    all[all.length - 1]?.focus();
  };

  const focusPreviousMenuItem = () => {
    const { all, focusedIndex } = getMenuItems();
    const nextFocusedIndex = focusedIndex - 1;
    const nextFocusedMenuItem = nextFocusedIndex < 0 ? all[all.length - 1] : all[nextFocusedIndex];

    nextFocusedMenuItem.focus();
  };

  const focusNextMenuItem = () => {
    const { all, focusedIndex } = getMenuItems();
    const nextFocusedIndex = focusedIndex + 1;
    const nextFocusedMenuItem = all[nextFocusedIndex % all.length];

    nextFocusedMenuItem.focus();
  };

  const handleKey = (event: KeyboardEvent) => {
    if (isKeyCombination(event)) {
      return;
    }

    const code = getCodeFromEvent(event);

    switch (code) {
      case 'ArrowUp':
        focusPreviousMenuItem();
        event.preventDefault();
        break;
      case 'ArrowDown':
        focusNextMenuItem();
        event.preventDefault();
        break;
      case 'Home':
        focusFirstMenuItem();
        event.preventDefault();
        break;
      case 'End':
        focusLastMenuItem();
        event.preventDefault();
        break;
      case 'Escape':
        closeMenu();
        break;
    }
  };
</script>

<div
  role="menu"
  class="menu"
  class:open
  class:bottom={displayBelow}
  {id}
  tabindex="-1"
  aria-label={label}
  bind:this={menuElement}
  on:keydown={handleKey}
>
  <slot />
</div>

<style module>
  [role='menu'].menu {
    position: absolute;
    bottom: 100%;
    right: 0;
    padding: 0.5em;

    background-color: #000000;
    background-color: var(--menu-background-color);

    border: 2px solid #5a5a5a;
    border-color: var(--menu-border-color);

    color: #f0f0f0; /* Fallback for older browsers */
    color: var(--primary-color);

    font-family: inherit;
    overflow-y: auto;

    max-height: 100vh;
    /* so that the menu does not touch the top of the controls layer container */
    --menu-gap: 8px;
    --controls-padding-bottom: clamp(8px, 5.6cqw - 1rem, 40px);
    --bottom-control-bar-height: 44px;
    max-height: calc(
      var(--content-layer-height) - var(--controls-padding-bottom) - var(--bottom-control-bar-height) - var(--menu-gap)
    );
    visibility: visible;
  }

  :global([data-mode='audio']) [role='menu'].menu {
    max-height: initial;
    max-height: bind(maxHeight);
  }

  :global([data-mode='audio']) [role='menu'].menu.bottom {
    bottom: initial;
  }

  [role='menu'].menu:not(.open) {
    visibility: hidden;
  }
</style>
